import React, { useState } from 'react';
import { config } from '../config';
import UserIcon from "../../../assets/images/user.webp";
import { Modal } from 'antd';

const InstructorSelection = ({
    instructors = [],
    selected_instructor,
    setSelectedInstructor,
    returning_instructor,
    availableInstructorIds,
    instructorIdsOnLeave = [],
    onInstructorNeedsNewTime,
}) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsData, setDetailsData] = useState(null);
    const [showAll, setShowAll] = useState(false);

    // Show every instructor assigned to the course, not just whoever happens to be
    // free at the exact time picked earlier - the card itself flags availability,
    // and picking someone who isn't free sends the customer back to Date & Time.
    const isAvailableForSelectedTime = (item) =>
        !availableInstructorIds || availableInstructorIds.includes(item.id);

    // Unlike a plain time-slot clash (tagged but still pickable, since the
    // customer just gets sent back to choose a new time), leave means this
    // instructor genuinely isn't working that day - hide them outright.
    const isOnLeave = (item) => instructorIdsOnLeave.includes(item.id);
    const bookableInstructors = instructors.filter((item) => !isOnLeave(item));

    const rawReturningMatch = returning_instructor
        ? instructors.find((i) => i.id === returning_instructor.id)
        : null;
    const returningMatch = rawReturningMatch && !isOnLeave(rawReturningMatch) ? rawReturningMatch : null;

    const handleSelect = (item) => {
        setSelectedInstructor(item);
        if (!isAvailableForSelectedTime(item) && onInstructorNeedsNewTime) {
            onInstructorNeedsNewTime(item);
        }
    };

    return (
        <div>
            <div className="booking-slot-header-row">
                <div>
                    <h2 className="booking-slot-title">Choose Instructor</h2>
                    <p className="booking-slot-subtitle">
                        {returningMatch
                            ? 'Welcome back! We found your previous instructor below.'
                            : 'Select an instructor for your lesson.'}
                    </p>
                </div>
            </div>

            {/* Returning student previous instructor */}
            {returningMatch && !showAll && (
                <div className="booking-instructor-section">
                    <div className="booking-instructor-section-label">
                        <i className="bi bi-person-check-fill me-2"></i>
                        Your previous instructor
                    </div>
                    <div className="booking-instructor-highlight">
                        <div
                            className={`booking-instructor-card returning${selected_instructor?.id === returningMatch.id ? ' selected' : ''}`}
                            onClick={() => handleSelect(returningMatch)}
                        >
                            <img
                                className="booking-instructor-card-photo"
                                src={returningMatch.image ? `${config.imageUrl}${returningMatch.image}` : UserIcon}
                                alt={returningMatch.name}
                            />
                            <h3>{returningMatch.name}</h3>
                            {isAvailableForSelectedTime(returningMatch) ? (
                                <span className="booking-instructor-availability">
                                    <i className="bi bi-check-circle-fill"></i>
                                    Available for your selected time
                                </span>
                            ) : (
                                <span className="booking-instructor-availability unavailable">
                                    <i className="bi bi-exclamation-circle-fill"></i>
                                    Not available at your selected time
                                </span>
                            )}
                            {returningMatch.location && (
                                <span className="booking-instructor-location">
                                    <i className="bi bi-geo-alt-fill me-1"></i>
                                    {returningMatch.location}
                                </span>
                            )}
                            <button
                                type="button"
                                className={`booking-instructor-card-btn${selected_instructor?.id === returningMatch.id ? ' selected' : ''}`}
                            >
                                {selected_instructor?.id === returningMatch.id ? (
                                    <><i className="bi bi-check-circle-fill me-2"></i>Selected</>
                                ) : (
                                    <>Continue with {returningMatch.name}</>
                                )}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="booking-instructor-change-toggle"
                            onClick={() => setShowAll(true)}
                        >
                            Change instructor
                        </button>
                    </div>
                </div>
            )}

            {/* All available instructors */}
            {(!returningMatch || showAll) && (
                <div className="booking-instructor-section">
                    {showAll && returningMatch && (
                        <button
                            type="button"
                            className="booking-instructor-back-btn"
                            onClick={() => setShowAll(false)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>
                            Back to your previous instructor
                        </button>
                    )}
                    <div className="booking-instructor-section-label">
                        <i className="bi bi-people-fill me-2"></i>
                        {showAll ? 'All available instructors' : 'Available instructors'}
                    </div>
                    <div className="booking-instructor-list">
                        {bookableInstructors.length === 0 ? (
                            <div className="booking-instructor-empty">
                                <i className="bi bi-person-x"></i>
                                <p>
                                    {instructors.length === 0
                                        ? 'No instructors are assigned to this course.'
                                        : 'All instructors for this course are on leave for your selected date.'}
                                </p>
                            </div>
                        ) : (
                            bookableInstructors.map((item) => (
                                <div
                                    key={item.id}
                                    className={`booking-instructor-card${selected_instructor?.id === item.id ? ' selected' : ''}`}
                                    onClick={() => handleSelect(item)}
                                >
                                    <img
                                        className="booking-instructor-card-photo"
                                        src={item.image ? `${config.imageUrl}${item.image}` : UserIcon}
                                        alt={item.name}
                                    />
                                    <h3>{item.name}</h3>
                                    {isAvailableForSelectedTime(item) ? (
                                        <span className="booking-instructor-availability">
                                            <i className="bi bi-check-circle-fill"></i>
                                            Available for your selected time
                                        </span>
                                    ) : (
                                        <span className="booking-instructor-availability unavailable">
                                            <i className="bi bi-exclamation-circle-fill"></i>
                                            Not available at your selected time
                                        </span>
                                    )}
                                    {item.location && (
                                        <span className="booking-instructor-location">
                                            <i className="bi bi-geo-alt-fill me-1"></i>
                                            {item.location}
                                        </span>
                                    )}
                                    {item.experience && (
                                        <span className="booking-instructor-experience">
                                            <i className="bi bi-award-fill me-1"></i>
                                            {item.experience} Years Experience
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        className={`booking-instructor-card-btn${selected_instructor?.id === item.id ? ' selected' : ''}`}
                                    >
                                        {selected_instructor?.id === item.id ? (
                                            <><i className="bi bi-check-circle-fill me-2"></i>Selected</>
                                        ) : (
                                            'Choose Me'
                                        )}
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Details modal */}
            <Modal
                open={detailsOpen}
                onCancel={() => setDetailsOpen(false)}
                width={800}
                centered
                footer={null}
                className="change-instructor-modal"
            >
                {detailsData && (
                    <div className="booking-instructor-modal">
                        <div className="row p-4">
                            <div className="col-md-4">
                                <img
                                    src={detailsData.image ? `${config.imageUrl}${detailsData.image}` : UserIcon}
                                    alt=""
                                    className="img-fluid rounded-4"
                                />
                                <h5 className="fw-bold mt-3 mb-2">{detailsData.name}</h5>
                                <h6 className="text-muted">{detailsData.experience} Years of Experience</h6>
                            </div>
                            <div className="col-md-8 instructor-data" style={{ maxHeight: 310, overflow: 'auto', scrollbarWidth: 'thin' }}>
                                <h5 className="mb-3 fs-4">Location: {detailsData.address}</h5>
                                <p>{detailsData.description}</p>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default InstructorSelection;
